import { USER_ROLES, type UserRole } from '@/types/user';

const USER_ROLE_VALUES = new Set<string>(Object.values(USER_ROLES));

/** Legacy Cognito/JWT role values → current Role values. */
const LEGACY_ROLE_MAP: Record<string, UserRole> = {
  NURSE: USER_ROLES.CLINICAL_SUPPORT,
  THERAPIST: USER_ROLES.CLINICAL_SUPPORT,
  HEALTH_COACH: USER_ROLES.CLINICAL_SUPPORT,
  RELATIONSHIP_MANAGER: USER_ROLES.CLINICAL_SUPPORT,
  RECEPTION: USER_ROLES.OPERATIONS_SUPPORT,
};

export const normalizeUserRole = (role: unknown): UserRole | null => {
  const normalized = String(role ?? '')
    .trim()
    .toUpperCase()
    .replace(/^ROLE_/, '');
  if (!normalized) return null;
  if (USER_ROLE_VALUES.has(normalized)) return normalized as UserRole;
  return LEGACY_ROLE_MAP[normalized] ?? null;
};

export const hasAnyUserRole = (role: unknown, ...roles: UserRole[]): boolean => {
  const normalized = normalizeUserRole(role);
  return normalized != null && roles.includes(normalized);
};

/** True when the stored role matches an allow-list entry after legacy names are normalized. */
export const matchesRoleAllowList = (role: unknown, allowedRoles: readonly string[]): boolean => {
  const normalized = normalizeUserRole(role);
  if (!normalized) return false;
  return allowedRoles.some((allowed) => normalizeUserRole(allowed) === normalized);
};

// Administrative roles that only a Super Admin may create, assign, edit, or
// manage. BUSINESS_DEVELOPMENT carries Admin-equivalent privileges, so it is
// treated as administrative to prevent privilege escalation. Mirrors the
// backend `ELEVATED_ROLES` in superclinic-api users/utils.py.
export const ELEVATED_ROLES: UserRole[] = [
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.SUPER_DOCTOR,
  USER_ROLES.ADMIN,
  USER_ROLES.BUSINESS_DEVELOPMENT,
];

export const isElevatedRole = (role: unknown): boolean => {
  const normalized = normalizeUserRole(role);
  return normalized != null && ELEVATED_ROLES.includes(normalized);
};

export const isSuperAdminRole = (role: unknown): boolean => {
  const normalized = normalizeUserRole(role);
  return normalized === USER_ROLES.SUPER_ADMIN || normalized === USER_ROLES.SUPER_DOCTOR;
};

export const INTERNAL_STAFF_ROLES = [
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.SUPER_DOCTOR,
  USER_ROLES.ADMIN,
  USER_ROLES.BUSINESS_DEVELOPMENT,
  USER_ROLES.DOCTOR,
  USER_ROLES.OPERATIONS_SUPPORT,
  USER_ROLES.CLINICAL_SUPPORT,
  USER_ROLES.NUTRITIONIST,
  USER_ROLES.PHARMACIST,
] as const;

export const ADMIN_ROLES = [
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.SUPER_DOCTOR,
  USER_ROLES.ADMIN,
  USER_ROLES.BUSINESS_DEVELOPMENT,
  USER_ROLES.DOCTOR,
] as const;

export const CLINICAL_ROLES = [
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.ADMIN,
  USER_ROLES.DOCTOR,
  USER_ROLES.CLINICAL_SUPPORT,
] as const;

export const SENIOR_ADMIN_ROLES = [USER_ROLES.SUPER_ADMIN, USER_ROLES.SUPER_DOCTOR] as const;

export const CHAT_STAFF_ROLES = [
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.SUPER_DOCTOR,
  USER_ROLES.ADMIN,
  USER_ROLES.BUSINESS_DEVELOPMENT,
  USER_ROLES.DOCTOR,
  USER_ROLES.CLINICAL_SUPPORT,
] as const;

export const REPORT_UPLOAD_STAFF_ROLES = [
  ...CHAT_STAFF_ROLES,
  USER_ROLES.OPERATIONS_SUPPORT,
] as const;

/** Roles the API allows to LIST ALL on cognito_users / users-simple. */
export const COGNITO_USER_LIST_ROLES = [
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.ADMIN,
  USER_ROLES.DOCTOR,
  USER_ROLES.BUSINESS_DEVELOPMENT,
  USER_ROLES.READ_ONLY,
] as const;

export const canListCognitoUsers = (role: unknown): boolean =>
  hasAnyUserRole(role, ...COGNITO_USER_LIST_ROLES);

export const isReportUploadStaffRole = (role: unknown): boolean =>
  hasAnyUserRole(role, ...REPORT_UPLOAD_STAFF_ROLES);

export const COHORT_RAW_EXPORT_ROLES = [
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.ADMIN,
  USER_ROLES.DOCTOR,
] as const;

export const canExportCohortRawData = (role: unknown): boolean =>
  hasAnyUserRole(role, ...COHORT_RAW_EXPORT_ROLES);
